import { useEffect, useRef, useState } from "react";
import { Headset, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useChatWidget } from "@/hooks/useChatWidget";
import { useChatPresence } from "@/hooks/useChatPresence";
import { useLang } from "@/context/LanguageContext";
import { WhatsAppIcon } from "@/components/icons/BrandIcons";
import { whatsappChatUrl } from "@/lib/social";

const TEASER_STORAGE_KEY = "eazy_chat_teaser_shown";
const TEASER_DELAY_MS = 4000;

// No real staff photo asset exists in this codebase, so presence is represented with a
// lucide "Headset" avatar (support-agent glyph) rather than a stock/fabricated photo.
const STATUS_META = {
  online: { dot: "bg-emerald-400", glowClass: "chat-glow-online", labelKey: "chat.onlineNow" },
  away: { dot: "bg-amber-400", glowClass: "chat-glow-away", labelKey: "chat.awayNow" },
  offline: { dot: "bg-red-400", glowClass: "chat-glow-offline", labelKey: "chat.offlineNow" },
};

function Avatar({ size = 44, iconSize = 20, statusDot, className = "" }) {
  return (
    <span className={`relative rounded-full bg-[var(--gold)]/15 grid place-items-center shrink-0 ${className}`} style={{ width: size, height: size }}>
      <Headset size={iconSize} className="text-[var(--gold)]" />
      {statusDot && (
        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[var(--surface)] ${statusDot}`} />
      )}
    </span>
  );
}

function Chooser({ status, t, onSelectChat, onSelectWhatsapp }) {
  const meta = STATUS_META[status];
  return (
    <div className="p-4 space-y-2.5" data-testid="chat-chooser">
      <p className="text-sm text-white/60 px-0.5 mb-1">{t("chat.chooserTitle")}</p>
      <button
        type="button"
        onClick={onSelectChat}
        className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-white/10 hover:border-[var(--gold)]/40 hover:bg-white/5 transition text-left"
        data-testid="chat-chooser-livechat"
      >
        <Avatar size={44} iconSize={19} statusDot={meta.dot} />
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-white">{t("chat.liveChatOption")}</span>
          <span className="block text-xs text-white/50">{t(meta.labelKey)}</span>
        </span>
      </button>
      <a
        href={whatsappChatUrl()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onSelectWhatsapp}
        className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-white/10 hover:border-emerald-400/40 hover:bg-white/5 transition text-left"
        data-testid="chat-chooser-whatsapp"
      >
        <span className="w-11 h-11 rounded-full bg-emerald-500/15 grid place-items-center shrink-0">
          <WhatsAppIcon size={19} className="text-emerald-400" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-white">{t("chat.whatsappOption")}</span>
        </span>
      </a>
    </div>
  );
}

function StartChatForm({ onStart, sending, t, status }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    try {
      await onStart(form);
    } catch {
      toast.error(t("chat.startError"));
    }
  };

  return (
    <form onSubmit={submit} className="p-4 space-y-3" data-testid="chat-start-form">
      <p className="text-sm text-white/60">
        {status === "offline" ? t("chat.greetingOffline") : t("chat.greetingOnline")}
      </p>
      <input
        required
        placeholder={t("chat.namePlaceholder")}
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        className="input-dark text-sm"
        data-testid="chat-start-name"
      />
      <input
        type="email"
        placeholder={t("chat.emailPlaceholder")}
        value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        className="input-dark text-sm"
        data-testid="chat-start-email"
      />
      <textarea
        required
        rows={3}
        placeholder={t("chat.messagePlaceholder")}
        value={form.message}
        onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
        className="input-dark text-sm"
        data-testid="chat-start-message"
      />
      <button type="submit" disabled={sending} className="btn-gold w-full justify-center text-sm py-2.5" data-testid="chat-start-submit">
        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {t("chat.startChat")}
      </button>
    </form>
  );
}

function ThreadView({ messages, loading, sending, onSend, t }) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    onSend(text);
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[240px] max-h-[340px]" data-testid="chat-thread">
        {loading && <div className="text-center py-6"><Loader2 size={20} className="animate-spin text-[var(--gold)] mx-auto" /></div>}
        {!loading && messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
                m.sender === "visitor"
                  ? "bg-[var(--gold)] text-[#0a0a0a] rounded-br-sm"
                  : "bg-white/10 text-white rounded-bl-sm"
              }`}
            >
              {m.sender === "staff" && <p className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5">{m.sender_name}</p>}
              <p className="whitespace-pre-wrap break-words">{m.message}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={submit} className="p-3 border-t border-white/10 flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("chat.typeMessage")}
          className="input-dark text-sm flex-1"
          data-testid="chat-message-input"
        />
        <button type="submit" disabled={sending || !draft.trim()} className="btn-gold px-3.5 shrink-0" data-testid="chat-message-send">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </>
  );
}

export default function ChatWidget() {
  const { t } = useLang();
  const { open, toggleOpen, conversationId, hasUnread, messages, loading, sending, startConversation, sendMessage } = useChatWidget();
  const status = useChatPresence();
  const [view, setView] = useState(conversationId ? "chat" : "chooser");
  const [showTeaser, setShowTeaser] = useState(false);
  const meta = STATUS_META[status];

  useEffect(() => {
    if (open || conversationId) return;
    if (sessionStorage.getItem(TEASER_STORAGE_KEY)) return;
    const timer = setTimeout(() => setShowTeaser(true), TEASER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, conversationId]);

  function dismissTeaser() {
    setShowTeaser(false);
    sessionStorage.setItem(TEASER_STORAGE_KEY, "1");
  }

  function handleWhatsappSelected() {
    dismissTeaser();
    if (open) toggleOpen();
  }

  function handleToggle() {
    if (open) {
      toggleOpen();
      return;
    }
    dismissTeaser();
    setView(conversationId ? "chat" : "chooser");
    toggleOpen();
  }

  return (
    <div className="fixed bottom-5 right-5 z-40" data-testid="chat-widget">
      {showTeaser && !open && (
        <div
          className="mb-3 max-w-[240px] ml-auto rounded-2xl border border-[var(--gold)]/20 bg-[var(--surface)] shadow-2xl p-3.5 flex items-start gap-2.5 animate-in fade-in slide-in-from-bottom-2 duration-200"
          data-testid="chat-teaser"
        >
          <Avatar size={36} iconSize={16} statusDot={meta.dot} />
          <p className="text-xs text-white/75 leading-snug flex-1 pt-1.5">
            {status === "offline" ? t("chat.teaserOffline") : t("chat.teaserOnline")}
          </p>
          <button onClick={dismissTeaser} className="text-white/40 hover:text-white shrink-0" aria-label="Dismiss" data-testid="chat-teaser-dismiss">
            <X size={13} />
          </button>
        </div>
      )}

      {open && (
        <div className="mb-3 w-[92vw] max-w-sm rounded-2xl overflow-hidden border border-[var(--gold)]/20 bg-[var(--surface)] shadow-2xl flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="flex items-center justify-between px-4 py-3 bg-radial-gold border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <Avatar size={36} iconSize={16} statusDot={meta.dot} />
              <div>
                <p className="text-sm font-bold text-white">{t("chat.supportTitle")}</p>
                <p className="text-xs text-white/50">{t(meta.labelKey)}</p>
              </div>
            </div>
            <button onClick={toggleOpen} className="text-white/60 hover:text-white" data-testid="chat-close">
              <X size={18} />
            </button>
          </div>

          {view === "chooser" && !conversationId ? (
            <Chooser status={status} t={t} onSelectChat={() => setView("chat")} onSelectWhatsapp={handleWhatsappSelected} />
          ) : !conversationId ? (
            <StartChatForm onStart={startConversation} sending={sending} t={t} status={status} />
          ) : (
            <ThreadView messages={messages} loading={loading} sending={sending} onSend={sendMessage} t={t} />
          )}
        </div>
      )}

      <button
        onClick={handleToggle}
        className={`relative w-14 h-14 rounded-full bg-[var(--gold)] text-[#0a0a0a] grid place-items-center hover:scale-105 transition ${
          open ? "shadow-2xl" : meta.glowClass
        }`}
        aria-label="Chat with us"
        data-testid="chat-toggle"
      >
        {open ? <X size={22} /> : <Headset size={22} />}
        {!open && hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-[var(--bg)]" data-testid="chat-unread-badge" />
        )}
      </button>
    </div>
  );
}
